(() => {
  if (window.rechargeMessageScriptLoaded) return;

  window.rechargeMessageScriptLoaded = true;

  const CART_SELECTOR =
    '#cart-drawer.bg-storefront-drawer-background';

  const CONFIG_ID =
    'recharge-minicart-config';

  const CONTAINER_ATTRIBUTE =
    'data-recharge-message-container';

  let observedCart = null;
  let updateTimer = null;
  let discoveryInterval = null;
  let cartActionTimerOne = null;
  let cartActionTimerTwo = null;
  let requestNumber = 0;
  let addRequestInProgress = false;

  function escapeHTML(value = '') {
    const element =
      document.createElement('div');

    element.textContent = String(value);

    return element.innerHTML;
  }

  function applyStyles(element, styles) {
    if (element) {
      element.style.cssText = styles;
    }
  }

  function getMessageIcon(icon) {
    if (icon === 'gift') {
      return `
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <rect
            x="3"
            y="8"
            width="18"
            height="13"
            rx="1"
            stroke="currentColor"
            stroke-width="1.5"
          ></rect>

          <path
            d="M12 8V21"
            stroke="currentColor"
            stroke-width="1.5"
          ></path>

          <path
            d="M3 12H21"
            stroke="currentColor"
            stroke-width="1.5"
          ></path>

          <path
            d="M12 8H7.75C6.23 8 5 6.77 5 5.25S6.23 2.5 7.75 2.5C10.25 2.5 12 8 12 8Z"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linejoin="round"
          ></path>

          <path
            d="M12 8H16.25C17.77 8 19 6.77 19 5.25S17.77 2.5 16.25 2.5C13.75 2.5 12 8 12 8Z"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linejoin="round"
          ></path>
        </svg>
      `;
    }

    if (icon === 'sale') {
      return `
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M20.5 13.5L13.5 20.5C12.72 21.28 11.45 21.28 10.67 20.5L3.5 13.33C3.18 13.01 3 12.58 3 12.13V5C3 3.9 3.9 3 5 3H12.13C12.58 3 13.01 3.18 13.33 3.5L20.5 10.67C21.28 11.45 21.28 12.72 20.5 13.5Z"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linejoin="round"
          ></path>

          <circle
            cx="7.5"
            cy="7.5"
            r="1.25"
            stroke="currentColor"
            stroke-width="1.5"
          ></circle>

          <path
            d="M10 15L15 10"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          ></path>

          <circle
            cx="10"
            cy="10"
            r="1"
            fill="currentColor"
          ></circle>

          <circle
            cx="15"
            cy="15"
            r="1"
            fill="currentColor"
          ></circle>
        </svg>
      `;
    }

    return '';
  }

  function getMessageRules() {
    const configElement =
      document.getElementById(CONFIG_ID);

    if (!configElement) {
      console.warn(
        '[Recharge messages] Configuration not found'
      );

      return [];
    }

    try {
      const config = JSON.parse(
        configElement.textContent
      );

      return Array.isArray(config.rules)
        ? config.rules
        : [];
    } catch (error) {
      console.error(
        '[Recharge messages] Invalid configuration:',
        error
      );

      return [];
    }
  }

  function findRechargeCart() {
    const roots = [document];

    for (
      let index = 0;
      index < roots.length;
      index++
    ) {
      const root = roots[index];

      const cart =
        root.querySelector(CART_SELECTOR);

      if (cart) return cart;

      root
        .querySelectorAll('*')
        .forEach((element) => {
          if (
            element.shadowRoot &&
            !roots.includes(element.shadowRoot)
          ) {
            roots.push(element.shadowRoot);
          }
        });
    }

    return null;
  }

  async function getShopifyCart() {
    const shopRoot =
      window.Shopify?.routes?.root || '/';

    const response = await fetch(
      `${shopRoot}cart.js`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(
        `Unable to load Shopify cart: ${response.status}`
      );
    }

    return response.json();
  }

  function ruleMatchesCart(rule, cart) {
    if (!rule.enabled) return false;

    const cartItems = Array.isArray(cart.items)
      ? cart.items
      : [];

    if (rule.layoutType === 'gift_progress') {
      const hasValidThreshold =
        Array.isArray(rule.thresholds) &&
        rule.thresholds.some(
          (threshold) =>
            Number(threshold.amount) > 0
        );

      return (
        hasValidThreshold &&
        cartItems.length > 0
      );
    }

    if (!rule.messageHTML?.trim()) {
      return false;
    }

    const configuredProductIDs = new Set(
      (rule.productIDs || []).map(String)
    );

    if (!configuredProductIDs.size) {
      return cartItems.length > 0;
    }

    const cartProductIDs = new Set(
      cartItems.map((item) =>
        String(item.product_id)
      )
    );

    if (rule.matchType === 'all') {
      return [...configuredProductIDs].every(
        (productID) =>
          cartProductIDs.has(productID)
      );
    }

    return [...configuredProductIDs].some(
      (productID) =>
        cartProductIDs.has(productID)
    );
  }

  function prepareRulesForCart(rules, cart) {
    const cartItems = Array.isArray(cart.items)
      ? cart.items
      : [];

    const cartProductIDs = new Set(
      cartItems.map((item) =>
        String(item.product_id)
      )
    );

    return rules
      .filter((rule) =>
        ruleMatchesCart(rule, cart)
      )
      .map((rule) => {
        const recommendedProductID =
          rule.recommendedProduct?.id;

        const productIsInCart =
          recommendedProductID &&
          cartProductIDs.has(
            String(recommendedProductID)
          );

        if (!productIsInCart) {
          return rule;
        }

        return {
          ...rule,
          recommendedProduct: null
        };
      });
  }

  function createRulesSignature(rules, cart) {
    return JSON.stringify({
      cartTotal: cart.total_price,
      currency: cart.currency,

      rules: rules.map((rule) => ({
        id: rule.id,
        layoutType: rule.layoutType,
        heading: rule.heading,
        icon: rule.icon,
        messageHTML: rule.messageHTML,
        link: rule.link,
        backgroundColor:
          rule.backgroundColor,
        textColor: rule.textColor,
        recommendedProduct:
          rule.recommendedProduct,
        thresholds: rule.thresholds,
        progressHeading:
          rule.progressHeading,
        completedHeading:
          rule.completedHeading,
        completedMessage:
          rule.completedMessage,
        trackColor: rule.trackColor,
        fillColor: rule.fillColor,
        milestoneColor:
          rule.milestoneColor
      }))
    });
  }

  function formatMoney(cents, currency) {
    const amount = Number(cents) / 100;

    const locale =
      document.documentElement.lang ||
      navigator.language ||
      'en-GB';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'GBP'
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }

  /*
   * Produces safe progress-message HTML.
   *
   * Only the value replacing [reward] becomes a link.
   * The rest of the sentence remains ordinary text.
   */
  function createProgressMessageHTML(
    message,
    values,
    rewardUrl
  ) {
    const tokenPattern =
      /(\[remaining\]|\[threshold\]|\[progress\]|\[reward\])/g;

    return String(message || '')
      .split(tokenPattern)
      .map((part) => {
        if (part === '[remaining]') {
          return escapeHTML(values.remaining);
        }

        if (part === '[threshold]') {
          return escapeHTML(values.threshold);
        }

        if (part === '[progress]') {
          return escapeHTML(
            `${values.percentage}%`
          );
        }

        if (part === '[reward]') {
          const rewardText =
            escapeHTML(
              values.reward || ''
            );

          if (!rewardUrl) {
            return rewardText;
          }

          return `
            <a
              href="${escapeHTML(rewardUrl)}"
              class="recharge-progress-reward-link"
            >
              ${rewardText}
            </a>
          `;
        }

        return escapeHTML(part);
      })
      .join('');
  }

  function createProgressElement(rule, cart) {
    const thresholds = (
      Array.isArray(rule.thresholds)
        ? rule.thresholds
        : []
    )
      .map((threshold) => ({
        ...threshold,

        amount: Math.max(
          Number(threshold.amount) || 0,
          0
        )
      }))
      .filter((threshold) =>
        threshold.amount > 0
      )
      .sort((first, second) =>
        first.amount - second.amount
      );

    if (!thresholds.length) {
      return null;
    }

    const cartTotal = Math.max(
      Number(cart.total_price) || 0,
      0
    );

    const highestThreshold =
      thresholds[thresholds.length - 1];

    const progressRatio = Math.min(
      cartTotal / highestThreshold.amount,
      1
    );

    const percentage = Math.round(
      progressRatio * 100
    );

    const unlockedThresholds =
      thresholds.filter(
        (threshold) =>
          cartTotal >= threshold.amount
      );

    const latestUnlocked =
      unlockedThresholds.length
        ? unlockedThresholds[
            unlockedThresholds.length - 1
          ]
        : null;

    const nextThreshold =
      thresholds.find(
        (threshold) =>
          cartTotal < threshold.amount
      ) || null;

    const isCompleted = !nextThreshold;

    const remainingAmount = nextThreshold
      ? Math.max(
          nextThreshold.amount - cartTotal,
          0
        )
      : 0;

    const backgroundColor =
      rule.backgroundColor || '#edebe5';

    const textColor =
      rule.textColor || '#111111';

    const trackColor =
      rule.trackColor || '#d8d3ca';

    const fillColor =
      rule.fillColor || '#111111';

    const milestoneColor =
      rule.milestoneColor || '#111111';

    const heading = isCompleted
      ? rule.completedHeading
      : rule.progressHeading;

    const rawMessage = isCompleted
      ? rule.completedMessage
      : nextThreshold.progressMessage;

    const currentReward =
      nextThreshold || highestThreshold;

    const tokenValues = {
      remaining: formatMoney(
        remainingAmount,
        cart.currency
      ),

      threshold: formatMoney(
        currentReward.amount,
        cart.currency
      ),

      percentage,

      reward:
        currentReward.rewardLabel
    };

    const messageHTML =
      createProgressMessageHTML(
        rawMessage,
        tokenValues,
        currentReward.rewardUrl
      );

    const unlockedTokenValues =
      latestUnlocked
        ? {
            remaining: formatMoney(
              0,
              cart.currency
            ),

            threshold: formatMoney(
              latestUnlocked.amount,
              cart.currency
            ),

            percentage,

            reward:
              latestUnlocked.rewardLabel
          }
        : null;

    const unlockedMessageHTML =
      latestUnlocked &&
      latestUnlocked.unlockedMessage
        ? createProgressMessageHTML(
            latestUnlocked.unlockedMessage,
            unlockedTokenValues,
            latestUnlocked.rewardUrl
          )
        : '';

    const milestoneHTML = thresholds
      .map((threshold) => {
        const position = Math.min(
          (
            threshold.amount /
            highestThreshold.amount
          ) * 100,
          100
        );

        const isUnlocked =
          cartTotal >= threshold.amount;

        const amountLabel = formatMoney(
          threshold.amount,
          cart.currency
        );

        return `
          <span
            class="recharge-progress-milestone"
            data-position="${position}"
            data-unlocked="${isUnlocked}"
            title="${escapeHTML(
              threshold.rewardLabel || ''
            )}: ${escapeHTML(amountLabel)}"
          >
            <span
              class="recharge-progress-milestone-dot"
            ></span>

            <span
              class="recharge-progress-milestone-label"
            >
              ${escapeHTML(amountLabel)}
            </span>
          </span>
        `;
      })
      .join('');

    const progressElement =
      document.createElement('div');

    progressElement.className =
      'recharge-gift-progress';

    progressElement.setAttribute(
      'data-recharge-message-rule',
      rule.id
    );

    progressElement.innerHTML = `
      <div class="recharge-progress-top">
        <div class="recharge-progress-content">
          ${
            heading
              ? `
                  <span
                    class="recharge-progress-heading"
                  >
                    ${escapeHTML(heading)}
                  </span>
                `
              : ''
          }

          ${
            unlockedMessageHTML &&
            !isCompleted
              ? `
                  <span
                    class="recharge-progress-unlocked-message"
                  >
                    ${unlockedMessageHTML}
                  </span>
                `
              : ''
          }

          ${
            messageHTML
              ? `
                  <span
                    class="recharge-progress-message"
                  >
                    ${messageHTML}
                  </span>
                `
              : ''
          }
        </div>

        <span
          class="recharge-progress-icon"
          aria-hidden="true"
        >
          ${getMessageIcon('gift')}
        </span>
      </div>

      <div class="recharge-progress-row">
        <div class="recharge-progress-scale">
          <div
            class="recharge-progress-track"
            role="progressbar"
            aria-label="${escapeHTML(
              heading || 'Free gift progress'
            )}"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow="${percentage}"
          >
            <span
              class="recharge-progress-fill"
            ></span>
          </div>

          ${milestoneHTML}
        </div>

        <span
          class="recharge-progress-percentage"
        >
          ${percentage}%
        </span>
      </div>
    `;

    applyStyles(
      progressElement,
      `
        display: flex;
        flex-direction: column;
        gap: 13px;
        width: 100%;
        box-sizing: border-box;
        margin: 0;
        padding: 15px 18px 16px;
        background-color: ${backgroundColor};
        color: ${textColor};
        border: 0;
        border-bottom: 1px solid color-mix(
          in srgb,
          ${textColor} 14%,
          transparent
        );
      `
    );

    applyStyles(
      progressElement.querySelector(
        '.recharge-progress-top'
      ),
      `
        display: grid;
        grid-template-columns:
          minmax(0, 1fr) 32px;
        align-items: center;
        column-gap: 12px;
        width: 100%;
        min-width: 0;
      `
    );

    applyStyles(
      progressElement.querySelector(
        '.recharge-progress-content'
      ),
      `
        display: flex;
        min-width: 0;
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
      `
    );

    applyStyles(
      progressElement.querySelector(
        '.recharge-progress-heading'
      ),
      `
        display: block;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.2;
        letter-spacing: 1.5px;
        text-transform: uppercase;
        text-align: left;
      `
    );

    applyStyles(
      progressElement.querySelector(
        '.recharge-progress-message'
      ),
      `
        display: block;
        min-width: 0;
        color: inherit;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.4;
        text-align: left;
      `
    );

    applyStyles(
      progressElement.querySelector(
        '.recharge-progress-unlocked-message'
      ),
      `
        display: block;
        min-width: 0;
        color: inherit;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.35;
        text-align: left;
      `
    );

    progressElement
      .querySelectorAll(
        '.recharge-progress-reward-link'
      )
      .forEach((rewardLink) => {
        applyStyles(
          rewardLink,
          `
            color: inherit;
            font: inherit;
            font-weight: 700;
            text-decoration: underline;
            text-underline-offset: 2px;
            cursor: pointer;
          `
        );
      });

    applyStyles(
      progressElement.querySelector(
        '.recharge-progress-icon'
      ),
      `
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
      `
    );

    applyStyles(
      progressElement.querySelector(
        '.recharge-progress-row'
      ),
      `
        display: grid;
        grid-template-columns:
          minmax(0, 1fr) auto;
        align-items: center;
        gap: 10px;
        width: 100%;
        min-width: 0;
      `
    );

    applyStyles(
      progressElement.querySelector(
        '.recharge-progress-scale'
      ),
      `
        position: relative;
        display: block;
        width: 100%;
        min-width: 0;
        padding-bottom: 22px;
      `
    );

    applyStyles(
      progressElement.querySelector(
        '.recharge-progress-track'
      ),
      `
        position: relative;
        display: block;
        width: 100%;
        height: 8px;
        overflow: hidden;
        background-color: ${trackColor};
        border-radius: 999px;
      `
    );

    applyStyles(
      progressElement.querySelector(
        '.recharge-progress-fill'
      ),
      `
        display: block;
        width: ${percentage}%;
        height: 100%;
        background-color: ${fillColor};
        border-radius: inherit;
        transition: width 300ms ease;
      `
    );

    applyStyles(
      progressElement.querySelector(
        '.recharge-progress-percentage'
      ),
      `
        display: block;
        min-width: 34px;
        color: inherit;
        font-size: 11px;
        font-weight: 600;
        line-height: 1;
        text-align: right;
      `
    );

    progressElement
      .querySelectorAll(
        '.recharge-progress-milestone'
      )
      .forEach((milestone) => {
        const position = Number(
          milestone.dataset.position
        );

        const isUnlocked =
          milestone.dataset.unlocked ===
          'true';

        applyStyles(
          milestone,
          `
            position: absolute;
            top: 4px;
            left: ${position}%;
            display: block;
            width: 0;
            height: 0;
            color: ${textColor};
            text-decoration: none;
            cursor: default;
            z-index: 2;
          `
        );

        applyStyles(
          milestone.querySelector(
            '.recharge-progress-milestone-dot'
          ),
          `
            position: absolute;
            top: 0;
            left: 0;
            display: block;
            width: 12px;
            height: 12px;
            box-sizing: border-box;
            background-color: ${
              isUnlocked
                ? fillColor
                : backgroundColor
            };
            border: 2px solid ${milestoneColor};
            border-radius: 50%;
            transform: translate(-50%, -50%);
          `
        );

        const labelTransform =
          position >= 95
            ? 'translateX(-100%)'
            : position <= 5
              ? 'translateX(0)'
              : 'translateX(-50%)';

        applyStyles(
          milestone.querySelector(
            '.recharge-progress-milestone-label'
          ),
          `
            position: absolute;
            top: 10px;
            left: 0;
            display: block;
            color: inherit;
            font-size: 9px;
            font-weight: 600;
            line-height: 1;
            white-space: nowrap;
            text-decoration: none;
            transform: ${labelTransform};
          `
        );
      });

    return progressElement;
  }

  function dispatchCartUpdate(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,

        detail: {
          cart,
          source:
            'recharge-recommended-product'
        }
      })
    );
  }

  /*
   * Schedules multiple updates because Recharge can replace
   * the cart drawer at different points after an action.
   */
  function scheduleUpdatesAfterCartAction() {
    window.clearTimeout(
      cartActionTimerOne
    );

    window.clearTimeout(
      cartActionTimerTwo
    );

    cartActionTimerOne =
      window.setTimeout(
        () => {
          if (!observedCart?.isConnected) {
            observedCart =
              findRechargeCart();
          }

          updateMessages();
        },
        450
      );

    cartActionTimerTwo =
      window.setTimeout(
        () => {
          if (!observedCart?.isConnected) {
            observedCart =
              findRechargeCart();
          }

          updateMessages();
        },
        1300
      );
  }

  async function addRecommendedProduct(
    button,
    variantControl
  ) {
    const variantID =
      variantControl?.value;

    if (
      !variantID ||
      button.disabled ||
      addRequestInProgress
    ) {
      return;
    }

    addRequestInProgress = true;

    const originalLabel =
      button.textContent;

    button.disabled = true;
    button.textContent = 'Adding…';
    button.style.opacity = '0.65';
    button.style.cursor = 'wait';

    try {
      const shopRoot =
        window.Shopify?.routes?.root || '/';

      const response = await fetch(
        `${shopRoot}cart/add.js`,
        {
          method: 'POST',

          headers: {
            Accept: 'application/json',
            'Content-Type':
              'application/json'
          },

          body: JSON.stringify({
            items: [
              {
                id: Number(variantID),
                quantity: 1
              }
            ]
          })
        }
      );

      const result =
        await response.json();

      if (!response.ok) {
        throw new Error(
          result.description ||
          result.message ||
          'Unable to add product'
        );
      }

      const updatedCart =
        await getShopifyCart();

      button.textContent = 'Added';
      button.style.opacity = '1';
      button.style.cursor = 'default';

      /*
       * Update the progress bar and hide the product card
       * immediately using the newly returned cart.
       */
      if (!observedCart?.isConnected) {
        observedCart =
          findRechargeCart();
      }

      const currentCartDrawer =
        observedCart;

      if (currentCartDrawer?.isConnected) {
        const updatedRules =
          prepareRulesForCart(
            getMessageRules(),
            updatedCart
          );

        renderMessages(
          currentCartDrawer,
          updatedRules,
          updatedCart
        );
      }

      /*
       * Notify Recharge, then refresh again after its own
       * drawer rendering has taken place.
       */
      dispatchCartUpdate(updatedCart);

      scheduleUpdate(50);
      scheduleUpdatesAfterCartAction();

      window.setTimeout(() => {
        button.disabled = false;
        button.textContent =
          originalLabel;
        button.style.cursor = 'pointer';
        addRequestInProgress = false;
      }, 1200);
    } catch (error) {
      console.error(
        '[Recharge messages] Add to cart failed:',
        error
      );

      button.textContent = 'Try again';
      button.style.opacity = '1';
      button.style.cursor = 'pointer';

      window.setTimeout(() => {
        button.disabled = false;
        button.textContent =
          originalLabel;
        addRequestInProgress = false;
      }, 1800);
    }
  }

  function createRecommendedProductCard(
    product,
    backgroundColor,
    textColor
  ) {
    if (
      !product ||
      !Array.isArray(product.variants)
    ) {
      return null;
    }

    const availableVariants =
      product.variants.filter(
        (variant) => variant.available
      );

    if (!availableVariants.length) {
      return null;
    }

    const selectedVariant =
      availableVariants[0];

    const hasMultipleVariants =
      product.variants.length > 1;

    const productCard =
      document.createElement('div');

    productCard.className =
      'recharge-recommended-product';

    const variantOptions =
      product.variants
        .map((variant) => {
          const unavailableText =
            variant.available
              ? ''
              : ' — Sold out';

          return `
            <option
              value="${escapeHTML(
                variant.id
              )}"
              ${
                variant.available
                  ? ''
                  : 'disabled'
              }
            >
              ${escapeHTML(
                variant.title
              )}
              — ${escapeHTML(
                variant.price
              )}
              ${unavailableText}
            </option>
          `;
        })
        .join('');

    productCard.innerHTML = `
      ${
        product.promptLabel?.trim()
          ? `
              <span
                class="recharge-recommended-label"
              >
                ${escapeHTML(
                  product.promptLabel
                )}
              </span>
            `
          : ''
      }

      <div
        class="recharge-recommended-row"
      >
        ${
          product.image
            ? `
                <a
                  href="${escapeHTML(
                    product.url
                  )}"
                  class="recharge-recommended-image"
                  aria-label="${escapeHTML(
                    product.title
                  )}"
                >
                  <img
                    src="${escapeHTML(
                      product.image
                    )}"
                    alt="${escapeHTML(
                      product.title
                    )}"
                    width="58"
                    height="58"
                    loading="lazy"
                  >
                </a>
              `
            : ''
        }

        <div
          class="recharge-recommended-content"
        >
          <a
            href="${escapeHTML(
              product.url
            )}"
            class="recharge-recommended-title"
          >
            ${escapeHTML(
              product.title
            )}
          </a>

          ${
            hasMultipleVariants
              ? `
                  <select
                    class="recharge-recommended-variant"
                    aria-label="Choose a variant"
                  >
                    ${variantOptions}
                  </select>
                `
              : `
                  <span
                    class="recharge-single-variant-price"
                  >
                    ${escapeHTML(
                      selectedVariant.price
                    )}
                  </span>

                  <input
                    type="hidden"
                    class="recharge-recommended-variant-id"
                    value="${escapeHTML(
                      selectedVariant.id
                    )}"
                  >
                `
          }
        </div>

        <button
          type="button"
          class="recharge-recommended-add"
        >
          ${escapeHTML(
            product.buttonLabel ||
            'Add to cart'
          )}
        </button>
      </div>
    `;

    applyStyles(
      productCard,
      `
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
        box-sizing: border-box;
        margin: 0;
        padding: 0 18px 16px;
        background-color:
          ${backgroundColor};
        color: ${textColor};
        border: 0;
      `
    );

    applyStyles(
      productCard.querySelector(
        '.recharge-recommended-label'
      ),
      `
        display: block;
        font-size: 11px;
        font-weight: 700;
        line-height: 1.2;
        letter-spacing: 1.4px;
        text-transform: uppercase;
      `
    );

    applyStyles(
      productCard.querySelector(
        '.recharge-recommended-row'
      ),
      `
        display: grid;
        grid-template-columns:
          ${product.image ? '58px' : ''}
          minmax(0, 1fr)
          auto;
        align-items: center;
        gap: 12px;
        width: 100%;
        min-width: 0;
        box-sizing: border-box;
        padding: 10px;
        background-color:
          rgba(255, 255, 255, 0.48);
        border: 1px solid color-mix(
          in srgb,
          ${textColor} 10%,
          transparent
        );
        border-radius: 10px;
      `
    );

    const imageLink =
      productCard.querySelector(
        '.recharge-recommended-image'
      );

    if (imageLink) {
      applyStyles(
        imageLink,
        `
          display: flex;
          align-items: center;
          justify-content: center;
          width: 58px;
          height: 58px;
          box-sizing: border-box;
          overflow: hidden;
          background-color:
            rgba(255, 255, 255, 0.34);
          color: inherit;
          border-radius: 5px;
          text-decoration: none;
        `
      );

      applyStyles(
        imageLink.querySelector('img'),
        `
          display: block;
          width: 52px;
          height: 52px;
          object-fit: contain;
          mix-blend-mode: multiply;
          border: 0;
        `
      );
    }

    applyStyles(
      productCard.querySelector(
        '.recharge-recommended-content'
      ),
      `
        display: flex;
        min-width: 0;
        flex-direction: column;
        align-items: stretch;
        justify-content: center;
        gap: 6px;
      `
    );

    applyStyles(
      productCard.querySelector(
        '.recharge-recommended-title'
      ),
      `
        display: block;
        width: 100%;
        overflow: hidden;
        color: inherit;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.25;
        text-decoration: none;
        text-overflow: ellipsis;
        white-space: nowrap;
      `
    );

    const variantSelect =
      productCard.querySelector(
        '.recharge-recommended-variant'
      );

    if (variantSelect) {
      applyStyles(
        variantSelect,
        `
          display: block;
          width: 100%;
          min-width: 0;
          min-height: 36px;
          box-sizing: border-box;
          margin: 0;
          padding: 7px 30px 7px 10px;
          background-color:
            rgba(255, 255, 255, 0.72);
          color: ${textColor};
          border: 1px solid color-mix(
            in srgb,
            ${textColor} 20%,
            transparent
          );
          border-radius: 4px;
          font: inherit;
          font-size: 11px;
          line-height: 1.2;
          text-overflow: ellipsis;
          cursor: pointer;
        `
      );

      variantSelect.value = String(
        selectedVariant.id
      );
    }

    applyStyles(
      productCard.querySelector(
        '.recharge-single-variant-price'
      ),
      `
        display: block;
        color: inherit;
        font-size: 11px;
        font-weight: 400;
        line-height: 1.2;
        opacity: 0.7;
      `
    );

    const variantControl =
      variantSelect ||
      productCard.querySelector(
        '.recharge-recommended-variant-id'
      );

    const addButton =
      productCard.querySelector(
        '.recharge-recommended-add'
      );

    applyStyles(
      addButton,
      `
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 82px;
        min-height: 36px;
        box-sizing: border-box;
        padding: 8px 14px;
        background-color: ${textColor};
        color: ${backgroundColor};
        border: 1px solid ${textColor};
        border-radius: 999px;
        font: inherit;
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
        letter-spacing: 0.35px;
        text-transform: uppercase;
        white-space: nowrap;
        cursor: pointer;
        transition:
          opacity 180ms ease,
          filter 180ms ease,
          transform 180ms ease;
      `
    );

    function setCardHover(isHovered) {
      const entireCard =
        productCard.closest(
          '.recharge-message-group'
        ) || productCard;

      entireCard.style.filter =
        isHovered
          ? 'brightness(0.96)'
          : 'none';

      addButton.style.transform =
        isHovered
          ? 'translateX(2px)'
          : 'translateX(0)';
    }

    addButton.addEventListener(
      'mouseenter',
      () => {
        if (!addButton.disabled) {
          setCardHover(true);
        }
      }
    );

    addButton.addEventListener(
      'mouseleave',
      () => {
        setCardHover(false);
      }
    );

    addButton.addEventListener(
      'focus',
      () => {
        if (!addButton.disabled) {
          setCardHover(true);
        }
      }
    );

    addButton.addEventListener(
      'blur',
      () => {
        setCardHover(false);
      }
    );

    addButton.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        addRecommendedProduct(
          addButton,
          variantControl
        );
      }
    );

    return productCard;
  }

  function createMessageElement(rule) {
    const isProductOffer =
      rule.layoutType ===
      'product_offer';

    const isTextLink =
      rule.layoutType ===
      'text_link';

    const hasLink =
      isTextLink &&
      Boolean(rule.link);

    const hasIcon =
      rule.icon === 'gift' ||
      rule.icon === 'sale';

    const backgroundColor =
      rule.backgroundColor ||
      '#edebe5';

    const textColor =
      rule.textColor ||
      '#111111';

    const message =
      document.createElement(
        hasLink ? 'a' : 'div'
      );

    message.setAttribute(
      'data-recharge-message-rule',
      rule.id
    );

    message.className =
      'recharge-custom-cart-message';

    if (hasLink) {
      message.href = rule.link;

      message.setAttribute(
        'aria-label',
        rule.heading ||
        'View cart benefit'
      );
    }

    message.innerHTML = `
      <span
        class="recharge-message-content"
      >
        ${
          rule.heading
            ? `
                <span
                  class="recharge-message-heading"
                >
                  ${escapeHTML(
                    rule.heading
                  )}
                </span>
              `
            : ''
        }

        <span
          class="recharge-message-text"
        >
          ${rule.messageHTML}
        </span>
      </span>

      ${
        hasIcon
          ? `
              <span
                class="recharge-message-icon"
                aria-hidden="true"
              >
                ${getMessageIcon(
                  rule.icon
                )}
              </span>
            `
          : ''
      }

      ${
        hasLink
          ? `
              <span
                class="recharge-message-arrow"
                aria-hidden="true"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 12H19"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  ></path>

                  <path
                    d="M13 6L19 12L13 18"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                </svg>
              </span>
            `
          : ''
      }
    `;

    applyStyles(
      message,
      `
        display: grid;
        grid-template-columns:
          minmax(0, 1fr)
          ${hasIcon ? '32px' : ''}
          ${hasLink ? '28px' : ''};
        align-items: center;
        column-gap: 12px;
        width: 100%;
        min-height: 78px;
        box-sizing: border-box;
        padding: 14px 18px;
        margin: 0;
        background-color:
          ${backgroundColor};
        color: ${textColor};
        border: 0;
        border-radius: 0;
        text-decoration: none;
        cursor: ${
          hasLink
            ? 'pointer'
            : 'default'
        };
        transition:
          filter 180ms ease;
      `
    );

    applyStyles(
      message.querySelector(
        '.recharge-message-icon'
      ),
      `
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
      `
    );

    applyStyles(
      message.querySelector(
        '.recharge-message-content'
      ),
      `
        display: flex;
        min-width: 0;
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
      `
    );

    applyStyles(
      message.querySelector(
        '.recharge-message-heading'
      ),
      `
        display: block;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.2;
        letter-spacing: 1.5px;
        text-transform: uppercase;
        text-align: left;
      `
    );

    const text =
      message.querySelector(
        '.recharge-message-text'
      );

    applyStyles(
      text,
      `
        display: block;
        min-width: 0;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.4;
        text-align: left;
      `
    );

    text
      ?.querySelectorAll('p')
      .forEach((paragraph) => {
        paragraph.style.margin = '0';
      });

    const arrow =
      message.querySelector(
        '.recharge-message-arrow'
      );

    if (arrow) {
      applyStyles(
        arrow,
        `
          display: flex;
          align-items: center;
          justify-content: flex-end;
          transition:
            transform 180ms ease;
        `
      );

      message.addEventListener(
        'mouseenter',
        () => {
          message.style.filter =
            'brightness(0.96)';

          arrow.style.transform =
            'translateX(3px)';
        }
      );

      message.addEventListener(
        'mouseleave',
        () => {
          message.style.filter =
            'none';

          arrow.style.transform =
            'translateX(0)';
        }
      );

      message.addEventListener(
        'focus',
        () => {
          message.style.filter =
            'brightness(0.96)';
        }
      );

      message.addEventListener(
        'blur',
        () => {
          message.style.filter =
            'none';

          arrow.style.transform =
            'translateX(0)';
        }
      );
    }

    const productCard =
      isProductOffer
        ? createRecommendedProductCard(
            rule.recommendedProduct,
            backgroundColor,
            textColor
          )
        : null;

    if (!productCard) {
      message.style.borderBottom =
        `1px solid color-mix(
          in srgb,
          ${textColor} 14%,
          transparent
        )`;

      return message;
    }

    const group =
      document.createElement('div');

    group.className =
      'recharge-message-group';

    applyStyles(
      group,
      `
        display: flex;
        flex-direction: column;
        width: 100%;
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        overflow: hidden;
        background-color:
          ${backgroundColor};
        color: ${textColor};
        border-bottom:
          1px solid color-mix(
            in srgb,
            ${textColor} 14%,
            transparent
          );
        transition:
          filter 180ms ease;
      `
    );

    group.append(
      message,
      productCard
    );

    return group;
  }

  function renderMessages(
    cartDrawer,
    matchingRules,
    cart
  ) {
    const existingContainer =
      cartDrawer.querySelector(
        `[${CONTAINER_ATTRIBUTE}]`
      );

    if (!matchingRules.length) {
      existingContainer?.remove();

      return;
    }

    const header =
      cartDrawer.querySelector(
        ':scope > header'
      );

    if (!header) return;

    const nextSignature =
      createRulesSignature(
        matchingRules,
        cart
      );

    let container =
      existingContainer;

    if (!container) {
      container =
        document.createElement('div');

      container.setAttribute(
        CONTAINER_ATTRIBUTE,
        ''
      );

      container.className =
        'recharge-custom-message-container';

      applyStyles(
        container,
        `
          display: flex;
          flex-direction: column;
          width: 100%;
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        `
      );

      header.insertAdjacentElement(
        'afterend',
        container
      );
    }

    if (
      container.dataset.rulesSignature ===
      nextSignature
    ) {
      return;
    }

    const messageElements =
      matchingRules
        .map((rule) => {
          if (
            rule.layoutType ===
            'gift_progress'
          ) {
            return createProgressElement(
              rule,
              cart
            );
          }

          return createMessageElement(rule);
        })
        .filter(Boolean);

    container.replaceChildren(
      ...messageElements
    );

    container.dataset.rulesSignature =
      nextSignature;
  }

  async function updateMessages() {
    /*
     * Recharge may replace the entire drawer after any cart
     * operation. Never retain a disconnected drawer.
     */
    if (!observedCart?.isConnected) {
      observedCart =
        findRechargeCart();
    }

    const cartDrawer =
      observedCart;

    if (!cartDrawer?.isConnected) {
      return;
    }

    const currentRequest =
      ++requestNumber;

    try {
      const cart =
        await getShopifyCart();

      const rules =
        getMessageRules();

      if (
        currentRequest !==
        requestNumber
      ) {
        return;
      }

      /*
       * The drawer can be replaced while cart.js is loading,
       * so check it again before rendering.
       */
      if (!observedCart?.isConnected) {
        observedCart =
          findRechargeCart();
      }

      const currentCartDrawer =
        observedCart;

      if (!currentCartDrawer?.isConnected) {
        return;
      }

      const preparedRules =
        prepareRulesForCart(
          rules,
          cart
        );

      renderMessages(
        currentCartDrawer,
        preparedRules,
        cart
      );
    } catch (error) {
      console.error(
        '[Recharge messages] Update failed:',
        error
      );
    }
  }

  function scheduleUpdate(delay = 250) {
    window.clearTimeout(updateTimer);

    updateTimer =
      window.setTimeout(
        updateMessages,
        delay
      );
  }

  function connectToCart(cartDrawer) {
    if (
      !cartDrawer ||
      cartDrawer === observedCart
    ) {
      return;
    }

    observedCart = cartDrawer;

    cartDrawer.addEventListener(
      'pointerup',
      (event) => {
        const target =
          event.target instanceof Element
            ? event.target
            : null;

        if (!target) return;

        /*
         * Our recommended product button schedules its own
         * updates.
         */
        if (
          target.closest(
            `[${CONTAINER_ATTRIBUTE}]`
          )
        ) {
          return;
        }

        scheduleUpdatesAfterCartAction();
      },
      true
    );

    cartDrawer.addEventListener(
      'keyup',
      (event) => {
        if (
          event.key !== 'Enter' &&
          event.key !== ' '
        ) {
          return;
        }

        const target =
          event.target instanceof Element
            ? event.target
            : null;

        if (
          !target ||
          target.closest(
            `[${CONTAINER_ATTRIBUTE}]`
          )
        ) {
          return;
        }

        scheduleUpdatesAfterCartAction();
      },
      true
    );

    scheduleUpdate(500);
  }

  function discoverCart() {
    if (observedCart?.isConnected) {
      return;
    }

    observedCart = null;

    connectToCart(
      findRechargeCart()
    );
  }

  const documentObserver =
    new MutationObserver(() => {
      if (!observedCart?.isConnected) {
        discoverCart();
      }
    });

  documentObserver.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );

  discoveryInterval =
    window.setInterval(
      discoverCart,
      1500
    );

  document.addEventListener(
    'cart:updated',
    () => {
      scheduleUpdate(50);
      scheduleUpdatesAfterCartAction();
    }
  );

  discoverCart();

  window.rechargeMessages = {
    update: updateMessages,
    getRules: getMessageRules,
    findCart: findRechargeCart,

    stop() {
      documentObserver.disconnect();

      window.clearInterval(
        discoveryInterval
      );

      window.clearTimeout(
        updateTimer
      );

      window.clearTimeout(
        cartActionTimerOne
      );

      window.clearTimeout(
        cartActionTimerTwo
      );

      observedCart = null;
      addRequestInProgress = false;
    }
  };
})();